// ─────────────────────────────────────────────────────────────────
// Glass-i — Goods Receipt Note (GRN) helpers (Stage 6P.3).
//
// Pure ESM module — tested by the frontend node:test runner.
//
// Frontend mirror of backend GRN math + payload shaping. The
// backend (`backend/src/services/erp/purchase/grnCalculation.service.js`
// and `goodsReceipt.service.js`) remains the source of truth for
// received_sqmt / received_sqft / received_total_length, rejected
// area math, PO pending balance, accepted/rejected reconciliation
// and the PARTIALLY_RECEIVED / ACCEPTED PO transitions. These
// helpers exist only to give the GRN editor instant feedback while
// the operator types; the API recomputes on every save.
//
// Mirrors the style of:
//   - frontend/src/pages/erp/purchase/purchaseHelpers.js
//     (option lists, normalize/build, validation shape)
//   - backend/src/services/erp/purchase/grnCalculation.service.js
//     (tolerant numeric coercion, round2/round4, line math,
//     snake_case ⇄ camelCase access).
// ─────────────────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────────
// Option lists — kept aligned with backend
// services/erp/purchase/grn.constants.js. Frozen so callers can't
// mutate the shared dropdowns by accident.
// ─────────────────────────────────────────────────────────────────

export const ITEM_TYPE_OPTIONS = Object.freeze([
  { value: 'GLASS',     label: 'Glass'      },
  { value: 'HARDWARE',  label: 'Hardware'   },
  { value: 'ACCESSORY', label: 'Accessory'  },
  { value: 'PROFILE',   label: 'Profile'    },
  { value: 'UPVC',      label: 'uPVC'       },
  { value: 'ALUMINIUM', label: 'Aluminium'  },
  { value: 'SERVICE',   label: 'Service'    },
  { value: 'JOBWORK',   label: 'Job Work'   },
  { value: 'OTHER',     label: 'Other'      }
]);

export const GRN_STATUS_OPTIONS = Object.freeze([
  { value: 'DRAFT',              label: 'Draft'              },
  { value: 'RECEIVED',           label: 'Received'           },
  { value: 'PARTIALLY_ACCEPTED', label: 'Partially Accepted' },
  { value: 'ACCEPTED',           label: 'Accepted'           },
  { value: 'CANCELLED',          label: 'Cancelled'          }
]);

export const GRN_INSPECTION_STATUS_OPTIONS = Object.freeze([
  { value: 'PENDING',            label: 'Pending'            },
  { value: 'ACCEPTED',           label: 'Accepted'           },
  { value: 'PARTIALLY_ACCEPTED', label: 'Partially Accepted' },
  { value: 'REJECTED',           label: 'Rejected'           }
]);

// Length-based UOMs — see backend grn.constants.LENGTH_BASED_UOMS.
export const LENGTH_BASED_UOMS = Object.freeze(['RFT', 'RMT', 'MTR']);

// Glass area UOMs — drive the sqmt ↔ sqft conversion path.
export const GLASS_AREA_UOMS = Object.freeze(['SQMT', 'SQFT']);

// 1 SQMT = 10.764 SQFT — matches backend SQMT_TO_SQFT.
export const SQMT_TO_SQFT = 10.764;


// ─────────────────────────────────────────────────────────────────
// Private constants used by the math helpers
// ─────────────────────────────────────────────────────────────────

const ITEM_TYPE_GLASS     = 'GLASS';
const ITEM_TYPE_PROFILE   = 'PROFILE';
const ITEM_TYPE_UPVC      = 'UPVC';
const ITEM_TYPE_ALUMINIUM = 'ALUMINIUM';

const LENGTH_ITEM_TYPES = new Set([
  ITEM_TYPE_PROFILE, ITEM_TYPE_UPVC, ITEM_TYPE_ALUMINIUM
]);

const LENGTH_UOMS_SET = new Set(LENGTH_BASED_UOMS);

const VALID_GRN_STATUSES        = new Set(GRN_STATUS_OPTIONS.map((o) => o.value));
const VALID_INSPECTION_STATUSES = new Set(GRN_INSPECTION_STATUS_OPTIONS.map((o) => o.value));
const VALID_ITEM_TYPES          = new Set(ITEM_TYPE_OPTIONS.map((o) => o.value));


// ─────────────────────────────────────────────────────────────────
// Tolerant numeric coercion — mirrors backend toNumber.
// Null / undefined / empty string / non-finite all collapse to 0.
// Never throws, never returns NaN.
// ─────────────────────────────────────────────────────────────────

function toNumber(value) {
  if (value === null || value === undefined) return 0;
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return 0;
    const n = Number(trimmed);
    return Number.isFinite(n) ? n : 0;
  }
  if (typeof value === 'boolean') return value ? 1 : 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

// Half-up rounding — defeats IEEE-754 surprises like 1.005 → 1.0049…
// so 1.005 rounds to 1.01, not 1.00, regardless of sign.
function roundHalfUp(value, decimals) {
  const n = toNumber(value);
  if (n === 0) return 0;
  const factor = Math.pow(10, decimals);
  const sign   = n < 0 ? -1 : 1;
  const abs    = Math.abs(n);
  const scaled = abs * factor + 1e-9;
  return (sign * Math.round(scaled)) / factor;
}

export function round2(n) { return roundHalfUp(n, 2); }
export function round4(n) { return roundHalfUp(n, 4); }


// ─────────────────────────────────────────────────────────────────
// Snake / camel access — lines arrive from both shapes (grid edits,
// API responses, tests). We probe both and never mutate the caller.
// ─────────────────────────────────────────────────────────────────

function pick(obj, snake, camel, fallback) {
  if (!obj || typeof obj !== 'object') return fallback;
  if (snake in obj && obj[snake] !== undefined) return obj[snake];
  if (camel in obj && obj[camel] !== undefined) return obj[camel];
  return fallback;
}

function pickStringUpper(obj, snake, camel) {
  const v = pick(obj, snake, camel, '');
  if (v === null || v === undefined) return '';
  return String(v).trim().toUpperCase();
}

function pickStringTrim(obj, snake, camel) {
  const v = pick(obj, snake, camel, '');
  if (v === null || v === undefined) return '';
  return String(v).trim();
}


// ─────────────────────────────────────────────────────────────────
// Receipt measurement helpers — exported for the GRN editor to
// drive instant recalculation as the operator types width / height
// / qty / length_value / rejected_qty. Mirrors the backend
// grnCalculation.service formulas bit-for-bit.
// ─────────────────────────────────────────────────────────────────

// Received glass area: widthMm × heightMm × qty ÷ 1,000,000 (mm² → m²).
export function calculateReceivedSqmt(widthMm, heightMm, qty) {
  const w = toNumber(widthMm);
  const h = toNumber(heightMm);
  const q = toNumber(qty);
  if (w <= 0 || h <= 0 || q <= 0) return 0;
  return round4((w * h * q) / 1000000);
}

export function calculateReceivedSqft(sqmt) {
  const s = toNumber(sqmt);
  if (s <= 0) return 0;
  return round4(s * SQMT_TO_SQFT);
}

// length_value × qty for length-driven items. UOM choice is
// validated upstream — this helper just multiplies.
export function calculateReceivedTotalLength(lengthValue, qty) {
  const l = toNumber(lengthValue);
  const q = toNumber(qty);
  if (l <= 0 || q <= 0) return 0;
  return round4(l * q);
}

// Rejected glass area uses the SAME width × height as the received
// line — only qty is swapped for rejectedQty so a 500×500 piece that
// was fully rejected occupies the same area as one that was accepted.
export function calculateRejectedSqmt(widthMm, heightMm, rejectedQty) {
  const w  = toNumber(widthMm);
  const h  = toNumber(heightMm);
  const rq = toNumber(rejectedQty);
  if (w <= 0 || h <= 0 || rq <= 0) return 0;
  return round4((w * h * rq) / 1000000);
}

export function calculateRejectedSqft(rejectedSqmt) {
  const s = toNumber(rejectedSqmt);
  if (s <= 0) return 0;
  return round4(s * SQMT_TO_SQFT);
}


// ─────────────────────────────────────────────────────────────────
// Stable client row ids — the grid needs a key for brand-new lines
// before the server has assigned a UUID. Mirrors the pr-c-N / po-c-N
// pattern used by purchaseHelpers.
// ─────────────────────────────────────────────────────────────────

let _rowIdCounter = 0;
function nextRowId(prefix) {
  _rowIdCounter += 1;
  return `${prefix}-c-${_rowIdCounter}`;
}


// ─────────────────────────────────────────────────────────────────
// Line normalisation — mirrors backend
// normalizeGrnLineMeasurements. Recomputes received_sqmt /
// received_sqft / received_total_length / rejected_sqmt /
// rejected_sqft per item_type and zeroes out irrelevant fields so
// stale grid state can't leak into header totals. Returns a NEW
// object carrying both casings, plus _clientRowId / _dirty.
// ─────────────────────────────────────────────────────────────────

export function normalizeGoodsReceiptLine(line) {
  const safe        = line && typeof line === 'object' ? line : {};
  const itemType    = pickStringUpper(safe, 'item_type', 'itemType');
  const receivedQty = toNumber(pick(safe, 'received_qty', 'receivedQty', 0));
  const rejectedQty = toNumber(pick(safe, 'rejected_qty', 'rejectedQty', 0));
  const acceptedQty = toNumber(pick(safe, 'accepted_qty', 'acceptedQty', 0));

  let widthMm   = toNumber(pick(safe, 'received_width_mm',     'receivedWidthMm',     0));
  let heightMm  = toNumber(pick(safe, 'received_height_mm',    'receivedHeightMm',    0));
  let lengthVal = toNumber(pick(safe, 'received_length_value', 'receivedLengthValue', 0));

  let receivedSqmt        = 0;
  let receivedSqft        = 0;
  let receivedTotalLength = 0;
  let rejectedSqmt        = 0;
  let rejectedSqft        = 0;

  if (itemType === ITEM_TYPE_GLASS) {
    receivedSqmt        = calculateReceivedSqmt(widthMm, heightMm, receivedQty);
    receivedSqft        = calculateReceivedSqft(receivedSqmt);
    rejectedSqmt        = calculateRejectedSqmt(widthMm, heightMm, rejectedQty);
    rejectedSqft        = calculateRejectedSqft(rejectedSqmt);
    receivedTotalLength = 0;
    // Length input is not meaningful on glass lines.
    lengthVal = 0;
  } else if (LENGTH_ITEM_TYPES.has(itemType)) {
    receivedTotalLength = calculateReceivedTotalLength(lengthVal, receivedQty);
    receivedSqmt        = 0;
    receivedSqft        = 0;
    rejectedSqmt        = 0;
    rejectedSqft        = 0;
    // Glass measurement inputs are not meaningful on length items.
    widthMm  = 0;
    heightMm = 0;
  } else {
    // HARDWARE / ACCESSORY / SERVICE / JOBWORK / OTHER — qty only.
    receivedSqmt        = 0;
    receivedSqft        = 0;
    receivedTotalLength = 0;
    rejectedSqmt        = 0;
    rejectedSqft        = 0;
    widthMm   = 0;
    heightMm  = 0;
    lengthVal = 0;
  }

  // Reuse existing _clientRowId if the editor already tagged the row;
  // otherwise mint a deterministic id from the server id when known.
  const cid =
    safe._clientRowId ||
    (safe.id ? `grn-${safe.id}` : nextRowId('grn'));

  return Object.assign({}, safe, {
    _clientRowId: cid,
    _dirty:       safe._dirty === true,

    item_type:               itemType || null,
    itemType:                itemType || null,

    received_qty:            receivedQty,
    receivedQty,
    accepted_qty:            acceptedQty,
    acceptedQty,
    rejected_qty:            rejectedQty,
    rejectedQty,

    received_width_mm:       widthMm,
    receivedWidthMm:         widthMm,
    received_height_mm:      heightMm,
    receivedHeightMm:        heightMm,
    received_length_value:   lengthVal,
    receivedLengthValue:     lengthVal,

    received_sqmt:           receivedSqmt,
    receivedSqmt,
    received_sqft:           receivedSqft,
    receivedSqft,
    received_total_length:   receivedTotalLength,
    receivedTotalLength,

    rejected_sqmt:           rejectedSqmt,
    rejectedSqmt,
    rejected_sqft:           rejectedSqft,
    rejectedSqft
  });
}


// ─────────────────────────────────────────────────────────────────
// API → editor shape — Goods Receipt Note (header + lines)
// ─────────────────────────────────────────────────────────────────

// Actor (received_by / inspected_by / cancelled_by) display resolver.
// The backend now sends a resolved `*Name` (display_name) alongside
// the raw id; prefer it. As a defensive fallback — older payloads,
// cached responses — accept a non-UUID raw value as the name, but
// NEVER surface a bare user UUID: those collapse to '' so the UI
// renders "—".
const GRN_ACTOR_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function actorDisplayName(resolvedName, rawValue) {
  const n = resolvedName == null ? '' : String(resolvedName).trim();
  if (n) return n;
  const r = rawValue == null ? '' : String(rawValue).trim();
  if (!r || GRN_ACTOR_UUID_RE.test(r)) return '';
  return r;
}

export function normalizeGoodsReceipt(api) {
  const safe  = api && typeof api === 'object' ? api : {};
  const lines = Array.isArray(safe.lines) ? safe.lines : [];
  return {
    id:                  safe.id                  || null,
    grnNo:               safe.grnNo               || safe.grn_no               || null,
    grnDate:             safe.grnDate             || safe.grn_date             || null,
    receivedDate:        safe.receivedDate        || safe.received_date        || null,
    status:              safe.status              || 'DRAFT',
    inspectionStatus:    safe.inspectionStatus    || safe.inspection_status    || 'PENDING',

    poId:                safe.poId                || safe.po_id                || null,
    poNumberSnapshot:    safe.poNumberSnapshot    || safe.po_number_snapshot   || null,
    poDateSnapshot:      safe.poDateSnapshot      || safe.po_date_snapshot     || null,

    supplierId:          safe.supplierId          || safe.supplier_id          || null,
    supplierName:        safe.supplierName        || safe.supplier_name        || null,
    supplierGstin:       safe.supplierGstin       || safe.supplier_gstin       || null,
    supplierContact:     safe.supplierContact     || safe.supplier_contact     || null,
    supplierAddress:     safe.supplierAddress     || safe.supplier_address     || null,

    deliveryLocation:    safe.deliveryLocation    || safe.delivery_location    || null,
    deliveryNoteRef:     safe.deliveryNoteRef     || safe.delivery_note_ref    || null,
    invoiceRef:          safe.invoiceRef          || safe.invoice_ref          || null,
    transporterRef:      safe.transporterRef      || safe.transporter_ref      || null,
    vehicleNo:           safe.vehicleNo           || safe.vehicle_no           || null,

    remarks:             safe.remarks             || null,
    inspectionRemarks:   safe.inspectionRemarks   || safe.inspection_remarks   || null,

    receivedBy:          safe.receivedBy          || safe.received_by          || null,
    receivedByName:      actorDisplayName(
                           safe.receivedByName  ?? safe.received_by_name,
                           safe.receivedBy      ?? safe.received_by),
    receivedAt:          safe.receivedAt          || safe.received_at          || null,
    inspectedBy:         safe.inspectedBy         || safe.inspected_by         || null,
    inspectedByName:     actorDisplayName(
                           safe.inspectedByName ?? safe.inspected_by_name,
                           safe.inspectedBy     ?? safe.inspected_by),
    inspectedAt:         safe.inspectedAt         || safe.inspected_at         || null,
    acceptedBy:          safe.acceptedBy          || safe.accepted_by          || null,
    acceptedAt:          safe.acceptedAt          || safe.accepted_at          || null,
    cancelledBy:         safe.cancelledBy         || safe.cancelled_by         || null,
    cancelledByName:     actorDisplayName(
                           safe.cancelledByName ?? safe.cancelled_by_name,
                           safe.cancelledBy     ?? safe.cancelled_by),
    cancelledAt:         safe.cancelledAt         || safe.cancelled_at         || null,
    cancellationReason:  safe.cancellationReason  || safe.cancellation_reason  || null,

    createdAt:           safe.createdAt           || safe.created_at           || null,
    updatedAt:           safe.updatedAt           || safe.updated_at           || null,

    lines: lines.map((ln) => normalizeGoodsReceiptLine(ln))
  };
}


// ─────────────────────────────────────────────────────────────────
// PO → GRN seeding — given the PO and the receipt balance, return
// GRN line drafts with received_qty defaulted to pending and
// accepted_qty mirroring received_qty (i.e. assume all received is
// accepted until the operator marks rejections). Width / height /
// length_value are copied from the PO line so the editor doesn't
// have to re-type them.
//
// `balance` may either be a single { pendingQty, ... } object (for
// the top of the list) OR a map keyed by po_line_id; when a per-line
// pending qty is supplied (`balance[poLineId].pendingQty`) we use
// it, otherwise we fall back to the PO line's ordered qty.
// ─────────────────────────────────────────────────────────────────

// Build a po_line_id → pendingQty lookup from the receipt-balance payload.
// The endpoint returns { lines: [{ poLineId, pendingQty, … }] }; we also
// accept a bare array of those rows, or the legacy map keyed by po line id.
function buildPendingLookup(balance) {
  const map = new Map();
  if (!balance || typeof balance !== 'object') return map;

  const arr = Array.isArray(balance)
    ? balance
    : (Array.isArray(balance.lines) ? balance.lines : null);

  if (arr) {
    for (const l of arr) {
      if (!l || typeof l !== 'object') continue;
      const id   = l.id != null ? l.id : pick(l, 'po_line_id', 'poLineId', null);
      const pend = pick(l, 'pending_qty', 'pendingQty', undefined);
      if (id != null && pend !== undefined) map.set(String(id), toNumber(pend));
    }
    return map;
  }

  // Legacy map shape: { [poLineId]: { pendingQty } }.
  for (const key of Object.keys(balance)) {
    const v = balance[key];
    if (v && typeof v === 'object') {
      const pend = pick(v, 'pending_qty', 'pendingQty', undefined);
      if (pend !== undefined) map.set(String(key), toNumber(pend));
    }
  }
  return map;
}

export function buildGrnFromPoLines(poLines, balance) {
  const list = Array.isArray(poLines) ? poLines : [];
  const pendingMap = buildPendingLookup(balance);
  const drafts = [];

  for (const po of list) {
    const safe       = po && typeof po === 'object' ? po : {};
    const poLineId   = safe.id || pick(safe, 'po_line_id', 'poLineId', null);
    const key        = poLineId != null ? String(poLineId) : null;
    const itemType   = pickStringUpper(safe, 'item_type', 'itemType');
    const uom        = pickStringUpper(safe, 'uom', 'uom');
    const orderedQty = toNumber(pick(safe, 'qty', 'qty', 0));

    // Use the per-line pending balance when known; otherwise fall back to the
    // ordered qty (e.g. the balance endpoint was unavailable).
    const hasPending = key != null && pendingMap.has(key);
    const pendingQty = hasPending ? pendingMap.get(key) : orderedQty;

    // Skip lines already fully received — mirrors the backend createFromPo,
    // which drops PO lines whose pending qty is 0 so they can't be over-received.
    if (hasPending && pendingQty <= 0) continue;

    const widthMm   = toNumber(pick(safe, 'width_mm',     'widthMm',     0));
    const heightMm  = toNumber(pick(safe, 'height_mm',    'heightMm',    0));
    const lengthVal = toNumber(pick(safe, 'length_value', 'lengthValue', 0));

    const draft = {
      po_line_id:              poLineId,
      poLineId,
      item_id:                 pick(safe, 'item_id',   'itemId',   null),
      itemId:                  pick(safe, 'item_id',   'itemId',   null),
      item_code:               pick(safe, 'item_code', 'itemCode', null),
      itemCode:                pick(safe, 'item_code', 'itemCode', null),
      item_name:               pick(safe, 'item_name', 'itemName', null),
      itemName:                pick(safe, 'item_name', 'itemName', null),
      item_type:               itemType || null,
      itemType:                itemType || null,
      uom:                     uom || null,

      // Defaults: received = pending, accepted = received, rejected = 0.
      received_qty:            pendingQty,
      receivedQty:             pendingQty,
      accepted_qty:            pendingQty,
      acceptedQty:             pendingQty,
      rejected_qty:            0,
      rejectedQty:             0,
      rejection_reason:        null,
      rejectionReason:         null,

      // Measurements copied from the PO line.
      received_width_mm:       widthMm,
      receivedWidthMm:         widthMm,
      received_height_mm:      heightMm,
      receivedHeightMm:        heightMm,
      received_length_value:   lengthVal,
      receivedLengthValue:     lengthVal,

      inspection_status:       'PENDING',
      inspectionStatus:        'PENDING',
      remarks:                 null
    };

    // Run through normalisation so the derived sqmt / sqft /
    // total_length are populated for the seeded row.
    drafts.push(normalizeGoodsReceiptLine(draft));
  }

  return drafts;
}


// ─────────────────────────────────────────────────────────────────
// Header summary — aggregates lines for the UI summary panel.
// Mirrors backend calculateGrnSummary so a draft GRN that hasn't
// been posted yet still shows the same totals the server will.
// ─────────────────────────────────────────────────────────────────

export function summarizeGrnLines(lines) {
  const list = Array.isArray(lines) ? lines : [];

  let totalLines               = 0;
  let totalReceivedQty         = 0;
  let totalAcceptedQty         = 0;
  let totalRejectedQty         = 0;
  let totalReceivedSqmt        = 0;
  let totalReceivedSqft        = 0;
  let totalReceivedTotalLength = 0;
  let totalRejectedSqmt        = 0;
  let totalRejectedSqft        = 0;

  for (const ln of list) {
    if (!ln || typeof ln !== 'object') continue;
    totalLines += 1;
    totalReceivedQty         += toNumber(pick(ln, 'received_qty',          'receivedQty',         0));
    totalAcceptedQty         += toNumber(pick(ln, 'accepted_qty',          'acceptedQty',         0));
    totalRejectedQty         += toNumber(pick(ln, 'rejected_qty',          'rejectedQty',         0));
    totalReceivedSqmt        += toNumber(pick(ln, 'received_sqmt',         'receivedSqmt',        0));
    totalReceivedSqft        += toNumber(pick(ln, 'received_sqft',         'receivedSqft',        0));
    totalReceivedTotalLength += toNumber(pick(ln, 'received_total_length', 'receivedTotalLength', 0));
    totalRejectedSqmt        += toNumber(pick(ln, 'rejected_sqmt',         'rejectedSqmt',        0));
    totalRejectedSqft        += toNumber(pick(ln, 'rejected_sqft',         'rejectedSqft',        0));
  }

  return {
    totalLines,
    totalReceivedQty:         round4(totalReceivedQty),
    totalAcceptedQty:         round4(totalAcceptedQty),
    totalRejectedQty:         round4(totalRejectedQty),
    totalReceivedSqmt:        round4(totalReceivedSqmt),
    totalReceivedSqft:        round4(totalReceivedSqft),
    totalReceivedTotalLength: round4(totalReceivedTotalLength),
    totalRejectedSqmt:        round4(totalRejectedSqmt),
    totalRejectedSqft:        round4(totalRejectedSqft)
  };
}


// ─────────────────────────────────────────────────────────────────
// Editor → payload builders. Strip client-only metadata
// (_clientRowId, _dirty), emit snake_case to mirror what the Joi
// validators expect, and omit nullish / empty optional fields so
// stripUnknown:true doesn't have to do extra work.
// ─────────────────────────────────────────────────────────────────

function trimOrNull(v) {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s ? s : null;
}

function dateOrNull(v) {
  if (v === null || v === undefined || v === '') return null;
  return v;
}

function omitNullish(obj) {
  const out = {};
  for (const k of Object.keys(obj)) {
    const v = obj[k];
    if (v === null || v === undefined) continue;
    if (typeof v === 'string' && v === '') continue;
    out[k] = v;
  }
  return out;
}

function buildGrnLinePayload(ln) {
  if (!ln || typeof ln !== 'object') return null;
  const norm     = normalizeGoodsReceiptLine(ln);
  const itemType = pickStringUpper(norm, 'item_type', 'itemType');
  const poLineId = trimOrNull(
    pick(ln, 'purchase_order_line_id', 'purchaseOrderLineId', null)
      || pick(ln, 'po_line_id', 'poLineId', null));
  return omitNullish({
    line_no:                toNumber(pick(ln, 'line_no', 'lineNo', undefined)) || undefined,
    // Canonical key the backend validates + joins on (grn.validation.js /
    // goodsReceipt.service.js key overrides by purchase_order_line_id).
    // `po_line_id` kept for backward-compat readers; backend stripUnknown
    // drops it harmlessly.
    purchase_order_line_id: poLineId,
    po_line_id:             poLineId,
    item_id:                trimOrNull(pick(ln, 'item_id',    'itemId',    null)),
    item_code:              trimOrNull(pick(ln, 'item_code',  'itemCode',  null)),
    item_name:              trimOrNull(pick(ln, 'item_name',  'itemName',  null)),
    item_type:              itemType || null,
    uom:                    pickStringUpper(ln, 'uom', 'uom') || null,

    received_qty:           toNumber(pick(norm, 'received_qty', 'receivedQty', 0)),
    accepted_qty:           toNumber(pick(norm, 'accepted_qty', 'acceptedQty', 0)),
    rejected_qty:           toNumber(pick(norm, 'rejected_qty', 'rejectedQty', 0)),
    rejection_reason:       trimOrNull(pick(ln, 'rejection_reason', 'rejectionReason', null)),

    received_width_mm:      itemType === ITEM_TYPE_GLASS
                              ? toNumber(norm.received_width_mm)
                              : null,
    received_height_mm:     itemType === ITEM_TYPE_GLASS
                              ? toNumber(norm.received_height_mm)
                              : null,
    received_length_value:  LENGTH_ITEM_TYPES.has(itemType)
                              ? toNumber(norm.received_length_value)
                              : null,

    inspection_status:      pickStringUpper(ln, 'inspection_status', 'inspectionStatus') || null,
    remarks:                trimOrNull(pick(ln, 'remarks', 'remarks', null))
  });
}

export function buildGoodsReceiptPayload(state) {
  const safe = state && typeof state === 'object' ? state : {};
  const lines = Array.isArray(safe.lines)
    ? safe.lines.map(buildGrnLinePayload).filter((l) => l !== null)
    : [];
  return omitNullish({
    grn_date:           dateOrNull(safe.grnDate          || safe.grn_date),
    received_date:      dateOrNull(safe.receivedDate     || safe.received_date),
    status:             trimOrNull(safe.status)          || 'DRAFT',
    inspection_status:  trimOrNull(safe.inspectionStatus || safe.inspection_status) || 'PENDING',

    // Canonical keys the backend validators require (grnCreateSchema /
    // grnUpdateSchema). `po_id` / `po_number_snapshot` kept for backward-
    // compat; backend stripUnknown drops the non-canonical pair.
    purchase_order_id:              trimOrNull(safe.poId || safe.po_id || safe.purchaseOrderId),
    purchase_order_number_snapshot: trimOrNull(safe.poNumberSnapshot || safe.po_number_snapshot),
    po_id:              trimOrNull(safe.poId             || safe.po_id),
    po_number_snapshot: trimOrNull(safe.poNumberSnapshot || safe.po_number_snapshot),

    supplier_id:        trimOrNull(safe.supplierId       || safe.supplier_id),
    supplier_name:      trimOrNull(safe.supplierName     || safe.supplier_name),
    supplier_gstin:     trimOrNull(safe.supplierGstin    || safe.supplier_gstin),
    supplier_contact:   trimOrNull(safe.supplierContact  || safe.supplier_contact),
    supplier_address:   trimOrNull(safe.supplierAddress  || safe.supplier_address),

    delivery_location:  trimOrNull(safe.deliveryLocation || safe.delivery_location),
    delivery_note_ref:  trimOrNull(safe.deliveryNoteRef  || safe.delivery_note_ref),
    invoice_ref:        trimOrNull(safe.invoiceRef       || safe.invoice_ref),
    transporter_ref:    trimOrNull(safe.transporterRef   || safe.transporter_ref),
    vehicle_no:         trimOrNull(safe.vehicleNo        || safe.vehicle_no),

    remarks:            trimOrNull(safe.remarks),
    inspection_remarks: trimOrNull(safe.inspectionRemarks || safe.inspection_remarks),
    lines
  });
}


// ─────────────────────────────────────────────────────────────────
// Status labels + tag colours. Colours align with the AntD Tag
// palette guidance in docs/23-antd-component-standards.md so the
// GRN grids match the rest of the control-plane chrome.
// ─────────────────────────────────────────────────────────────────

const STATUS_LABELS = {
  DRAFT:              'Draft',
  RECEIVED:           'Received',
  PARTIALLY_ACCEPTED: 'Partially Accepted',
  ACCEPTED:           'Accepted',
  CANCELLED:          'Cancelled',
  // Inspection-status labels (shared map so callers can use the
  // same helper for both columns).
  PENDING:            'Pending',
  REJECTED:           'Rejected'
};

const STATUS_COLORS = {
  DRAFT:              'default',
  RECEIVED:           'processing',
  PARTIALLY_ACCEPTED: 'gold',
  ACCEPTED:           'success',
  CANCELLED:          'default',
  PENDING:            'default',
  REJECTED:           'error'
};

export function statusLabel(status) {
  if (!status) return '';
  const key = String(status).trim().toUpperCase();
  return STATUS_LABELS[key] || key;
}

export function statusColor(status) {
  if (!status) return 'default';
  const key = String(status).trim().toUpperCase();
  return STATUS_COLORS[key] || 'default';
}

// Hex dot colours for the calm "dot + label" status presentation used by
// the GRN list + form (instead of a filled tag that washes the cell).
// Aligned with the PO / RFQ status-dot palette for cross-module
// consistency. Presentation only — does not affect any status logic.
const STATUS_DOT_HEX = {
  DRAFT:              '#94a3b8',
  RECEIVED:           '#1877f2',
  PARTIALLY_ACCEPTED: '#f59e0b',
  ACCEPTED:           '#16a34a',
  CANCELLED:          '#dc2626',
  PENDING:            '#94a3b8',
  REJECTED:           '#dc2626'
};

export function statusDotColor(status) {
  if (!status) return '#94a3b8';
  const key = String(status).trim().toUpperCase();
  return STATUS_DOT_HEX[key] || '#94a3b8';
}


// ─────────────────────────────────────────────────────────────────
// Per-status capability gates — used by the page to enable/disable
// toolbar buttons. The backend remains the authority; these guards
// only hide CTAs the operator can't possibly succeed with.
// ─────────────────────────────────────────────────────────────────

function grnStatus(grn) {
  if (!grn || typeof grn !== 'object') return null;
  const s = String(grn.status || '').trim().toUpperCase();
  return s || null;
}

export function canEditGrn(grn) {
  // Edits to header / lines are only permitted while still in
  // DRAFT — once RECEIVED the line data is locked in.
  return grnStatus(grn) === 'DRAFT';
}

export function canReceiveGrn(grn) {
  // DRAFT → RECEIVED transition.
  return grnStatus(grn) === 'DRAFT';
}

export function canInspectGrn(grn) {
  // Inspection runs after the goods are RECEIVED but before they're
  // ACCEPTED into stock.
  return grnStatus(grn) === 'RECEIVED';
}

export function canAcceptGrn(grn) {
  // Accept (full or partial) is permitted from RECEIVED.
  return grnStatus(grn) === 'RECEIVED';
}

export function canCancelGrn(grn) {
  const s = grnStatus(grn);
  return s === 'DRAFT' || s === 'RECEIVED';
}

export function canDeleteGrn(grn) {
  // Hard-delete (soft-delete in DB) only from DRAFT — anything
  // already RECEIVED has supplier-facing implications.
  return grnStatus(grn) === 'DRAFT';
}


// ─────────────────────────────────────────────────────────────────
// Last-mile validation — runs locally before opening the network
// call. Returns the same { ok, errors: [{ field, message,
// clientRowId? }] } shape glassCategoryHelpers / purchaseHelpers use
// so the page can share the AntD Form.ErrorList renderer.
//
// Mirrors the Joi shape rules in grn.validation.js + the receipt
// reconciliation rules in grnCalculation.validateReceivedAgainstPo
// — the backend remains the authority and will reject anything we
// miss (including PO pending-balance overflow, which we cannot
// check without the PO context).
// ─────────────────────────────────────────────────────────────────

function pushErr(errors, field, message, clientRowId) {
  const e = { field, message };
  if (clientRowId) e.clientRowId = clientRowId;
  errors.push(e);
}

function isISODateLike(v) {
  if (v === null || v === undefined || v === '') return false;
  if (v instanceof Date) return !Number.isNaN(v.getTime());
  const d = new Date(v);
  return !Number.isNaN(d.getTime());
}

function compareDates(a, b) {
  if (!isISODateLike(a) || !isISODateLike(b)) return 0;
  return new Date(a).getTime() - new Date(b).getTime();
}

export function validateGoodsReceiptLine(line) {
  const errors = [];
  if (!line || typeof line !== 'object') {
    pushErr(errors, 'line', 'Invalid line.');
    return { ok: false, errors };
  }
  const cid      = line._clientRowId;
  const itemType = pickStringUpper(line, 'item_type', 'itemType');
  const uom      = pickStringUpper(line, 'uom', 'uom');
  const name     = trimOrNull(pick(line, 'item_name', 'itemName', null));

  const receivedQty     = toNumber(pick(line, 'received_qty', 'receivedQty', 0));
  const acceptedQty     = toNumber(pick(line, 'accepted_qty', 'acceptedQty', 0));
  const rejectedQty     = toNumber(pick(line, 'rejected_qty', 'rejectedQty', 0));
  const rejectionReason = pickStringTrim(line, 'rejection_reason', 'rejectionReason');
  const inspectionStat  = pickStringUpper(line, 'inspection_status', 'inspectionStatus');

  if (!name) {
    pushErr(errors, 'item_name', 'Stock item is required.', cid);
  }
  if (!itemType) {
    pushErr(errors, 'item_type', 'Item type is required.', cid);
  } else if (!VALID_ITEM_TYPES.has(itemType)) {
    pushErr(errors, 'item_type',
      `Item type must be one of: ${[...VALID_ITEM_TYPES].join(', ')}.`, cid);
  }
  if (!uom) {
    pushErr(errors, 'uom', 'UOM is required.', cid);
  }
  if (receivedQty <= 0) {
    pushErr(errors, 'received_qty', 'Received qty must be greater than 0.', cid);
  }
  if (acceptedQty < 0) {
    pushErr(errors, 'accepted_qty', 'Accepted qty cannot be negative.', cid);
  }
  if (rejectedQty < 0) {
    pushErr(errors, 'rejected_qty', 'Rejected qty cannot be negative.', cid);
  }

  // GLASS lines must carry width + height so receipt area math works.
  if (itemType === ITEM_TYPE_GLASS) {
    const w = toNumber(pick(line, 'received_width_mm',  'receivedWidthMm',  0));
    const h = toNumber(pick(line, 'received_height_mm', 'receivedHeightMm', 0));
    if (w <= 0) {
      pushErr(errors, 'received_width_mm',
        'Received width is required for glass lines.', cid);
    }
    if (h <= 0) {
      pushErr(errors, 'received_height_mm',
        'Received height is required for glass lines.', cid);
    }
  } else if (LENGTH_ITEM_TYPES.has(itemType) && LENGTH_UOMS_SET.has(uom)) {
    const len = toNumber(pick(line, 'received_length_value', 'receivedLengthValue', 0));
    if (len <= 0) {
      pushErr(errors, 'received_length_value',
        'Received length is required for length-based items.', cid);
    }
  }

  // accepted + rejected must reconcile to received (epsilon to
  // forgive penny-level UI rounding).
  const reconciled = round4(acceptedQty + rejectedQty);
  if (reconciled - receivedQty > 1e-6) {
    pushErr(errors, 'accepted_qty',
      `Accepted (${acceptedQty}) + Rejected (${rejectedQty}) cannot exceed Received (${receivedQty}).`,
      cid);
  }

  // Rejection requires a reason.
  if (rejectedQty > 0 && rejectionReason === '') {
    pushErr(errors, 'rejection_reason',
      'Rejection reason is required when any quantity is rejected.', cid);
  }

  // Inspection status, if set, must be one of the enums.
  if (inspectionStat && !VALID_INSPECTION_STATUSES.has(inspectionStat)) {
    pushErr(errors, 'inspection_status',
      `Inspection status must be one of: ${[...VALID_INSPECTION_STATUSES].join(', ')}.`, cid);
  }

  return { ok: errors.length === 0, errors };
}

export function validateGoodsReceipt(state) {
  const errors = [];
  if (!state || typeof state !== 'object') {
    pushErr(errors, 'state', 'Goods receipt is required.');
    return { ok: false, errors };
  }

  const grnDate      = state.grnDate      || state.grn_date;
  const receivedDate = state.receivedDate || state.received_date;
  const status       = state.status;
  const inspection   = state.inspectionStatus || state.inspection_status;
  const supplierName = trimOrNull(state.supplierName || state.supplier_name);

  if (!grnDate) {
    pushErr(errors, 'grn_date', 'GRN date is required.');
  }
  if (!supplierName) {
    pushErr(errors, 'supplier_name', 'Supplier name is required.');
  }
  if (status && !VALID_GRN_STATUSES.has(String(status).toUpperCase())) {
    pushErr(errors, 'status',
      `Status must be one of: ${[...VALID_GRN_STATUSES].join(', ')}.`);
  }
  if (inspection && !VALID_INSPECTION_STATUSES.has(String(inspection).toUpperCase())) {
    pushErr(errors, 'inspection_status',
      `Inspection status must be one of: ${[...VALID_INSPECTION_STATUSES].join(', ')}.`);
  }
  if (grnDate && receivedDate && compareDates(receivedDate, grnDate) < 0) {
    pushErr(errors, 'received_date',
      'Received date cannot be before GRN date.');
  }

  const lines = Array.isArray(state.lines) ? state.lines : [];
  if (lines.length === 0) {
    pushErr(errors, 'lines', 'Add at least one line to the goods receipt.');
  } else {
    for (const ln of lines) {
      const r = validateGoodsReceiptLine(ln);
      if (!r.ok) errors.push(...r.errors);
    }
  }

  return { ok: errors.length === 0, errors };
}
